import { useCallback, useEffect, useRef, useState } from 'react'
import throttle from 'lodash/throttle'

/**
 * Observe an element's dimensions with ResizeObserver.
 * Returns the element's React ref and latest DOMRectReadOnly, throttled to update at most once every `wait` ms (or 250 by default).
 */

export function useElementDimensions<T extends HTMLElement>(
  wait: number = 250
): [(node: T | null) => void, DOMRectReadOnly | null] {
  const nodeRef = useRef<T | null>(null)
  const [rect, setRect] = useState<DOMRectReadOnly | null>(null)

  const setNode = useCallback((node: T | null) => {
    nodeRef.current = node
  }, [])

  useEffect(() => {
    const node = nodeRef.current

    if (!node) return

    const emit = (entry: DOMRectReadOnly) => setRect(entry)
    const throttledUpdate = throttle(emit, wait, {
      leading: true,
      trailing: true
    })

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        throttledUpdate(entry.contentRect)
      }
    })

    observer.observe(node)

    return () => {
      observer.disconnect()
      throttledUpdate.cancel()
    }
  }, [wait])

  return [setNode, rect]
}
